import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const MoneyChitPlans = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { chitPlanDetails } = location.state || {};
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!chitPlanDetails || !chitPlanDetails.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 flex items-center justify-center p-6">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-200 max-w-md">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📊</span>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Money Chit Plans</h3>
          <p className="text-gray-500 mb-6">No money chit plans available for this profile.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-200"
          >
            ← Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const moneyPlans = chitPlanDetails;
  const moneyPlan = moneyPlans[currentIndex];

  const {
    name,
    chitValue,
    months,
    startDate,
    endDate,
    fullChitBidderAmount,
    startFullChitNonBidder,
    monthlyDecrease,
    members,
    planStatus,
    chitInstallmentDetailsList = [],
    settlements = [],
    withdrawalStatus
  } = moneyPlan;

  // Get the latest settlement if available
  const latestSettlement = settlements && settlements.length > 0 ? settlements[0] : null;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': 'bg-green-100 text-green-800 border border-green-200',
      'pending': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'rejected': 'bg-red-100 text-red-800 border border-red-200',
      'completed': 'bg-blue-100 text-blue-800 border border-blue-200',
      'fullSettlement': 'bg-green-100 text-green-800 border border-green-200',
      'partialSettlement': 'bg-blue-100 text-blue-800 border border-blue-200',
      'Paid': 'bg-green-100 text-green-800 border border-green-200',
      'NotPaid': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'Pending': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'Upcoming': 'bg-gray-100 text-gray-800 border border-gray-200'
    };
    
    return statusConfig[status] || 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  // Transform chitInstallmentDetailsList for table - FIXED MAPPING
  const formattedSchedule = (moneyPlan.chitInstallmentDetailsList || []).map((installment) => ({
    month: installment.installmentNumber,
    dueDate: installment.installmentDate,
    halfChitAmount: installment.installmentHalfChitNonBiderAmount || installment.installmentHalfChitBiderAmount,
    fullChitAmount: installment.installmentFullChitNonBiderAmount || installment.installmentFullChitBiderAmount,
    status: installment.paymentStatus || "Upcoming",
    paidDate: installment.paidDate,
    transactionId: installment.transactionId,
    // Additional fields from your JSON
    installmentFullChitBiderAmount: installment.installmentFullChitBiderAmount,
    installmentFullChitNonBiderAmount: installment.installmentFullChitNonBiderAmount,
    paidAmount: installment.paidAmount,
    penaltyAmount: installment.penaltyAmount,
    balanceAmount: installment.balanceAmount
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-teal-600 to-blue-700 rounded-2xl shadow-2xl overflow-hidden mb-8">
          <div className="p-8 text-white">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl lg:text-4xl font-bold mb-2">
                  {name || "Money Chit Plan"}
                </h1>
                <p className="text-teal-100 text-lg">Complete Plan Details & Payment Schedule</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl"
                >
                  ← Back to Profile
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-8 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full"></div>
              <h2 className="text-xl font-bold text-gray-800">Plan Navigation</h2>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((i) => i - 1)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${
                  currentIndex === 0
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-md hover:shadow-lg"
                }`}
              >
                ← Previous
              </button>
              
              <div className="text-center">
                <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Plan {currentIndex + 1} of {moneyPlans.length}
                </span>
              </div>
              
              <button
                disabled={currentIndex === moneyPlans.length - 1}
                onClick={() => setCurrentIndex((i) => i + 1)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${
                  currentIndex === moneyPlans.length - 1
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-md hover:shadow-lg"
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        {/* Plan Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Plan Name</span>
                <span className="font-semibold text-gray-800">{name || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Chit Value</span>
                <span className="font-bold text-blue-600">{formatCurrency(chitValue)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Duration</span>
                <span className="font-semibold text-gray-800">{months} months</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-800">Financial Details</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Members</span>
                <span className="font-semibold text-gray-800">{members}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Full Chit Amount</span>
                <span className="font-bold text-green-600">{formatCurrency(fullChitBidderAmount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Monthly Decrease</span>
                <span className="font-semibold text-gray-800">₹{monthlyDecrease || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-2xl border border-purple-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-gradient-to-r from-purple-500 to-violet-500 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-800">Timeline & Status</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Start Date</span>
                <span className="font-semibold text-gray-800">{formatDate(startDate)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">End Date</span>
                <span className="font-semibold text-gray-800">{formatDate(endDate)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Status</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(planStatus)}`}>
                  {planStatus || "Active"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Withdrawal/Settlement Section */}
        {latestSettlement && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
              <h2 className="text-2xl font-bold text-gray-800">Settlement Details</h2>
              {withdrawalStatus && (
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadge(withdrawalStatus)}`}>
                  {withdrawalStatus}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-5 rounded-xl border border-orange-200">
                <div className="text-sm text-gray-600 font-medium mb-2">Settlement Date</div>
                <div className="text-lg font-bold text-orange-600">{formatDate(latestSettlement.withdrawalDate)}</div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200">
                <div className="text-sm text-gray-600 font-medium mb-2">Settlement Amount</div>
                <div className="text-lg font-bold text-green-600">{formatCurrency(latestSettlement.paidAmount)}</div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
                <div className="text-sm text-gray-600 font-medium mb-2">Processed By</div>
                <div className="text-lg font-semibold text-blue-600 truncate">{latestSettlement.processedBy || "System"}</div>
              </div>
            </div>

            {latestSettlement.notes && (
              <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600 font-medium mb-2">Notes</div>
                <div className="text-gray-800">{latestSettlement.notes}</div>
              </div>
            )}
          </div>
        )}

        {/* Payment Schedule */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-3 h-8 bg-gradient-to-r from-teal-500 to-green-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-800">Payment Schedule</h2>
            <span className="bg-gradient-to-r from-teal-500 to-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              {formattedSchedule.length} Installments
            </span>
          </div>

          {formattedSchedule.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <th className="p-4 text-left font-semibold text-gray-700">Month</th>
                        <th className="p-4 text-left font-semibold text-gray-700">Due Date</th>
                        <th className="p-4 text-left font-semibold text-gray-700">Non-Bidder Amount</th>
                        <th className="p-4 text-left font-semibold text-gray-700">Bidder Amount</th>
                        <th className="p-4 text-left font-semibold text-gray-700">Paid Date</th>
                        <th className="p-4 text-left font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formattedSchedule.map((schedule, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="p-4 font-medium text-gray-900">{schedule.month}</td>
                          <td className="p-4 text-gray-700">{formatDate(schedule.dueDate)}</td>
                          <td className="p-4 font-semibold text-blue-600">
                            {formatCurrency(schedule.installmentFullChitNonBiderAmount)}
                          </td>
                          <td className="p-4 font-semibold text-green-600">
                            {formatCurrency(schedule.installmentFullChitBiderAmount)}
                          </td>
                          <td className="p-4 text-gray-700">
                            {schedule.paidDate ? formatDate(schedule.paidDate) : "-"}
                          </td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(schedule.status)}`}>
                              {schedule.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {formattedSchedule.map((schedule, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-5 border border-gray-200">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className="text-lg font-bold text-gray-800">Installment #{schedule.month}</span>
                        <div className="text-sm text-gray-600">Due: {formatDate(schedule.dueDate)}</div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(schedule.status)}`}>
                        {schedule.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-white p-3 rounded-xl border border-gray-200">
                        <div className="text-xs text-gray-600">Non-Bidder Amount</div>
                        <div className="font-bold text-blue-600">
                          {formatCurrency(schedule.installmentFullChitNonBiderAmount)}
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-gray-200">
                        <div className="text-xs text-gray-600">Bidder Amount</div>
                        <div className="font-bold text-green-600">
                          {formatCurrency(schedule.installmentFullChitBiderAmount)}
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-gray-200">
                        <div className="text-xs text-gray-600">Paid Date</div>
                        <div className="font-medium text-gray-800">
                          {schedule.paidDate ? formatDate(schedule.paidDate) : "-"}
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-xl border border-gray-200">
                        <div className="text-xs text-gray-600">Transaction ID</div>
                        <div className="font-medium text-gray-800 text-xs truncate">
                          {schedule.transactionId || "-"}
                        </div>
                      </div>
                    </div>

                    {/* Additional details for mobile */}
                    {(schedule.paidAmount || schedule.penaltyAmount || schedule.balanceAmount) && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-200">
                        {schedule.paidAmount && (
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Paid</div>
                            <div className="text-sm font-semibold text-green-600">
                              {formatCurrency(schedule.paidAmount)}
                            </div>
                          </div>
                        )}
                        {schedule.penaltyAmount && (
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Penalty</div>
                            <div className="text-sm font-semibold text-red-600">
                              {formatCurrency(schedule.penaltyAmount)}
                            </div>
                          </div>
                        )}
                        {schedule.balanceAmount && (
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Balance</div>
                            <div className="text-sm font-semibold text-orange-600">
                              {formatCurrency(schedule.balanceAmount)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">💸</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Installments Found</h3>
              <p className="text-gray-500">This plan doesn't have any installment data yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoneyChitPlans;